import { useCallback, useEffect, useState } from "react";
import resolveChartConfigurationState from "./chartConfigurationStateResolver";

import getChartActionByCardType from "../actionResolver";
import ThemeManager from "../ThemeManager";
import light from "src/assets/style/themes/light";
import dark from "src/assets/style/themes/dark";
import { useEventBus } from "src/hooks";
import { ON_SIMPLE_CHART_COLOR_CHANGE } from "src/hooks/EventBus/eventTypes";

/**
 * Classe gerenciadora de temas para gráficos que possuem a capacidade de herdar alteração de cores
 * do gráfico simples.
 */
export default class ConfigurableChartThemeManager extends ThemeManager {
  constructor(
    analyzeId,
    surveyId,
    withData,
    chartRef,
    appTheme,
    chartType,
    cardType,
  ) {
    super(analyzeId, surveyId, withData, chartRef, appTheme, chartType, cardType);

    this._eventBus = useEventBus();
    // Tema de cores que será consumido pelo gráfico, por quê setamos um estado se já temos
    // this._payload ? R: Como o gerenciador de tema configurável tem a capacidade
    // de setar tema de cores herdados pelo gráfico simples, nós criamos um estado "consumedPayload"
    // que representa as cores do gráfico à nivel visual. Quando cores são herdadas no gráfico
    // nós não queremos que o payload de configuração real mude, por isso criamos um payload-espelho,
    // apenas para representar as cores consumidas pelo gráfico.
    const [consumedPayload, setConsumedPayload] = useState({});
    // Cores herdadas do gráfico simples
    const [simpleChartColorConfig, setSimpleChartColorConfig] = useState({});
    // armazena as legendas dos gráficos
    const [legends, setLegends] = useState([])
    // Externalizando os estados para a instância
    this._consumedPayload = consumedPayload;
    this._setConsumedPayload = setConsumedPayload;
    this._simpleChartColorConfig = simpleChartColorConfig;
    this._setSimpleChartColorConfig = setSimpleChartColorConfig;
    this._legends = legends;
    this._setLegends = setLegends;

    // Key correspondente do redux da configuração do gráfico
    this._reduxConfigKey = resolveChartConfigurationState(this._cardType);

    // Binds

    // Evento que será disparado quando o gráfico simples notificar uma mudança de cores
    this._onSimpleColorChange = this._onSimpleColorChange.bind(this);

    // Inherited from ThemeManager
    this._recalculateColorArray = this._recalculateColorArray.bind(this);
    this._setChartConfiguration = this._setChartConfiguration.bind(this);
    this._setChartSeries = this._setChartSeries.bind(this);
    this._setActions = this._setActions.bind(this);
    this.getConfig = this.getConfig.bind(this);

    // Inscreve-se no objeto que escuta mudanças do gráfico simples
    this._subscribeToSimpleChartColorChanges();

    // Utilização do hook que armazena os dados de series do gráfico atual
    this._setChartSeries();
    // Seta as configurações do gráfico atrelados ao redux (payload | fontsSizes | error | ... |)
    this._setChartConfiguration(this._reduxConfigKey);
    // Prepara as ações de atualização de configuração do gráfico (updateChartColor | updateFontSize | updateColorScheme)
    // para utilização na instância
    this._setActions();

    // Binding
    this._updateThemeColorsOnSimpleChartChange = this._updateThemeColorsOnSimpleChartChange.bind(this);
    this._updateThemeColorsOnConfigChange = this._updateThemeColorsOnConfigChange.bind(this);
    this._onChartInitialized = this._onChartInitialized.bind(this);
    this.setOnChartInitializedEventHandler = this.setOnChartInitializedEventHandler.bind(this);
    //
    this.setOnChartInitializedEventHandler(this._onChartInitialized);

    this._setChartInitializationState = this._setChartInitializationState.bind(this);
    this._setChartInitializationState();

    this._updateThemeColorsOnSimpleChartChange();
    this._updateThemeColorsOnConfigChange();

    this.setLegends()
  }

  _onChartInitialized() {
    this._setConsumedPayload({});
  }

  /**
   * Atualiza o tema de cores do gráfico atual de acordo com as regras de herança definidas.
   * Em definição, os gráficos de cruzamentos (multiplos, simples) e gráficos evolutivos devem herdar
   * alterações de cores feitas no gráfico simples, com as seguintes condições:
   *
   * Ao alterar uma cor no gráfico simples, seja por color picker ou esquema de cores (menu meatball) essa cor e/ou tema de cores
   * deve ser herdado nos demais gráficos citados quando:
   *  - O gráfico herdeiro não possui configurações de cores definidas
   *  - O gráfico herdeiro possui configuração de cores definidas, mas é feito um reset do seu esquema de cores
   *    a ação de reset ocorre quando muda-se o esquema de cores no menu meatball para "padrão".
   *
   * Caso uma alteração de cor seja feita num gráfico herdeiro, seja por color picker ou esquema de cores
   * (sem considerar a ação de reset), esse gráfico irá possuir seu próprio tema de cores, e isso implica
   * dizer que ele não herda mais nenhuma alteração de cor feita no gráfico simples.
   */
  _updateThemeColorsOnSimpleChartChange() {
    useEffect(() => {
      if (this._chartInitialized) {
        // Caso não exista configuração de cores no gráfico porém existem dados plotados
        if (!this._hasColorConfigs()) {
          // Recupera o objeto de cores herdado do gráfico simples
          const inheritedColors = { ...this._simpleChartColorConfig };
          const colorsChanged = JSON.stringify(this._consumedPayload) !== JSON.stringify(inheritedColors);
          
          // Só atualiza o estado de cores caso tenha ocorrido uma mudança no estado de cores
          if (colorsChanged || this._error) {
            // Recalcula as cores consumidas pelo gráfico atual
            this._recalculateColorArray(inheritedColors);
            // Atualiza o payload de cores do gráfico
            this._setConsumedPayload(inheritedColors);
          }
        }
      }
    }, [
      this._payload,
      this._chartInitialized,
      this._simpleChartColorConfig,
      this._error
    ]);
  }

  /**
   * Atualiza as configurações de cores do gráfico apenas quando o gráfico possui algumas configuração
   * salva, a lógica também aplica uma nova RN para consumir cores correspondentes do tema padrão da aplicação
   * (light/dark), independente do gráfico possuir sua própria temática. Para os comentários posteriores,
   * tenha em mente que quando eu menciono "configuração de cores", eu me refiro às configurações armazenadas
   * no estado do redux, que são referentes ao "this"._payload
   */
  _updateThemeColorsOnConfigChange() {
    useEffect(() => {
      // Verifica se o gráfico foi inicializado e possui configurações de cores salvas
      if (this._chartInitialized && this._hasColorConfigs()) {
        // Cria uma cópia da configuração de cor atual do gráfico
        const colorsPayloadWithThemeConfigs = {
          ...this._payload.chartsColors,
        };

        // Recupera as keys do objeto de configuração de cores: Object { key: enunciado; value: color; }
        const payloadKeys = Object.keys(colorsPayloadWithThemeConfigs);
        // Recupera o array de cores do objeto de configuração de cores
        const payloadColorsArray = Object.values(colorsPayloadWithThemeConfigs);

        /**
         * Inicialmente, é feito um filtro que recupera as cores atuais do payload de configuração que
         * estão incluídas no tema da aplicação (light/dark). Em seguida, essas cores filtratadas são
         * mapeadas em um objeto que possui a cor e seu enunciado correspondente, para facilitar consultas
         * posteriormente.
         */
        const themeColorsInPayload = payloadColorsArray.filter(
          (color) => light.colors.charts.colors.includes(color) || dark.colors.charts.colors.includes(color)
        ).map((themeColor) => ({
          color: themeColor,
          enunciado: payloadKeys[payloadColorsArray.indexOf(themeColor)],
        }));

        // Irá aplicar a lógica de consumo de cores do tema do app com base na configuração atual
        // de cores do gráfico.
        if (themeColorsInPayload.length) {
          // Recupera o array de cores reverso, se o tema atual é light, o array de cores do tema dark
          // será recuperado, e vice-versa.
          const reversedThemeColors = this._appTheme.title === dark.title
          ? light.colors.charts.colors
          : dark.colors.charts.colors;

          // Percorre as cores do tema da aplicação que foram salvas no objeto de configuração de cores do gráfico.
          themeColorsInPayload.forEach((themeMeta) => {
            const { color, enunciado } = themeMeta;
            // Caso retorne um número >= 0, significa que a cor verificada já pertence ao tema atual
            // da aplicação
            const indexInsideCurrentTheme = this._appTheme.colors.charts.colors.indexOf(color);
            // Caso a condição acima seja verdadeira, podemos pular esta etapa e apenas ir para a próxima iteração
            // porque esta cor não precisa ser trocada por sua cor correspondente ao tema reverso.
            if (indexInsideCurrentTheme >= 0) return;
            // Se a verificação anterior retornou um índice negativo (-1), isso significa que a cor configurada
            // não pertence ao tema atual da aplicação, e sim ao seu tema reverso, entende reverso como
            // light -> reverso em dark, e dark -> reverso em light

            // então, aqui recuperamos o índice da cor iterada no array de cores do tema reverso
            const colorIndexInReversedTheme = reversedThemeColors.indexOf(color);
            // em seguida, pegamos a cor correspondente no array de cores atual da aplicação
            const reversedColor = this._appTheme.colors.charts.colors[colorIndexInReversedTheme];
            // e substituimos no payload de cópia criado anteriormente
            colorsPayloadWithThemeConfigs[enunciado] = reversedColor;
          });
        }

        // Este é o objeto final de cores que será utilizado para atualizar as cores do gráfico
        const updatedColors = {
          ...this._consumedPayload,
          ...colorsPayloadWithThemeConfigs,
        };

        // Verificação simples para evitar setStates desnecessários
        if ((JSON.stringify(updatedColors) !== JSON.stringify(this._consumedPayload)) || this._error) {
          // Recalculamos o array de cores consumido pelo gráfico, isto que causa o re-load de cores.
          this._recalculateColorArray({ ...updatedColors });
          // Atualizamos o estado do objeto de cores visíveis
          this._setConsumedPayload({ ...updatedColors });
        }
      }
    }, [this._payload, this._chartInitialized, this._appTheme.title, this._error]);
  }

  // Função responsável por setar o estado dos enunciados das alternativas de um gráfico
  setLegends() {
    useEffect(() => {
      const refIsArray = this._chartRef && Array.isArray(this._chartRef.current)
      const currentRef = 
        (this._chartRef && refIsArray && this._chartRef.current[0]) ||
        (!refIsArray && this._chartRef.current);
        
      if(currentRef && !this._legends.length) {
        this._setLegends(
          currentRef.props.option.legend
          .data.map(legend => typeof(legend) === 'string' ? legend : legend.name)
        ) 
      }
    }, [this._chartRef, this._legends])
  }

  /**
   * Inscreve-se para escutar mudanças de cores do gráfico simples.
   */
  _subscribeToSimpleChartColorChanges() {
    // useEffect: Quando o card do gráfico é montado, o subscribe do gráfico é feito para receber notificações
    // do gráfico simples.
    useEffect(() => {
      this._eventBus.subscribe(
        ON_SIMPLE_CHART_COLOR_CHANGE,
        this._onSimpleColorChange,
        false
      );
    }, []);
  }


  /**
   *
   * @param {object} simpleChartColorsConfig Objeto de cores notificado por uma alteração de cores
   *  no gráfico simples.
   */
  _onSimpleColorChange(simpleChartColorsConfig = null) {
    const inheritanceColorsPayloadChanged = JSON.stringify(this._simpleChartColorConfig)
      !== JSON.stringify(simpleChartColorsConfig);

    if (simpleChartColorsConfig && inheritanceColorsPayloadChanged) {
      // Setamos as cores herdadas pelo gráfico simples
      this._setSimpleChartColorConfig({ ...simpleChartColorsConfig });
    }
  }

  // Callback enviando para o componente de chart para atualizar as cores de configuração do chart
  // na API
  _updateChartColors() {
    return useCallback((enunciadoAlterado, newColor) => {
      const { updateChartColorsAction } = getChartActionByCardType(this._cardType);

      const chartConfigDataPayload = {
        ...this._payload,
      };

      // Atualiza as cores alteradas ao objeto de cores baseado no tipo de gráfico atual
      // O gráfico de barras horizontais possui uma configuração de cores diferente dos demais, por isso
      // uma lógica única foi desenvolvido para ele.
      chartConfigDataPayload.chartsColors[enunciadoAlterado] = newColor;

      // Os cards que utilizam a classe ConfigurableChartThemeManager o utilizam para implementar
      // as regras de herança, aqui nós iteramos o payload de cores consumido, que é constantemente
      // atualizado de acordo com alterações no gráfico simples e atualizamos o payload
      // de cores do gráfico herdeiro, isso garante que o gráfico herdeiro montará seu próprio
      // esquema de cores quando um update for feito.
      Object.keys(this._consumedPayload).forEach((enunciado) => {
        if (!chartConfigDataPayload.chartsColors[enunciado] && this._legends.includes(enunciado)) {
          chartConfigDataPayload.chartsColors[enunciado] = this._consumedPayload[enunciado];
        }
      });

      // Copia o payload carregado da API com objeto de configuração de cores e fontes dos gráficos
      // Submete esta mudança para a API
      updateChartColorsAction(
        this._surveyId,
        this._analyzeId,
        chartConfigDataPayload,
        this._colorPickerColors,
        newColor,
        this._apiHasNoConfigs,
        this._dispatch,
        // Success callback (on 200 status code)
        () => {
          this._recalculateColorArray({ ...chartConfigDataPayload.chartsColors });
        },
      );
    }, [
      this._payload,
      this._colorPickerColors,
      this._dispatch,
      this._analyzeId,
      this._surveyId,
      this._apiHasNoConfigs,
      this._consumedPayload,
      this._appTheme.title,
      this._legends,
    ]);
  }
}
